import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Box, Typography, List, Slide, IconButton, useMediaQuery } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../types/global/routes.types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EmptySearchBlock } from '../EsSidebar/EsSearch/EmptySearchBlock';
import CloseIcon from '@mui/icons-material/Close';
import RoomItem from '../RoomItem/RoomItem';
import { cutoffAndAddPlus, getJWtDetails } from '../../utils/AppUtils';
import { batch, useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
    toggleAvatarMessageModal,
    toggleFloatingMessageModal,
    toggleIsMessageModalExpanded,
    toggleMessagenModal,
    setMessagesExpandedReturnTarget,
} from '../../redux/appConfigurator/action';
import { getMessagesForSelectedRoom, getMyRooms, retrieveUserRooms } from '../../redux/messages/actions';
import { fetchingRoomsSelector, roomsSelector } from '../../selectors/selectors';
import RoomItemSkeleton from '../RoomItem/RoomItemSkeleton';
import { unreadMessageTotalCount } from '../ChatBubble/selector';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import ExpandIcon from '../CustomSVGIcons/ExpandIcon';
import QuickMessageIcon from '../CustomSVGIcons/QuickMessageIcon';
import { ComposeMessageIcon } from '../CustomSVGIcons/ComposeMessageIcon';
import { useMessageContext } from '../../hook/useMessageContext';
import {
    avatarMessageOpenSelector,
    floatingMessageModalOpenSelector,
    isMessageModalExpandedSelector,
} from '../../redux/appConfigurator/selectors';
import { FloatingConversationView } from './FloatingConversationView';
import { MessageDialogue } from '../MessageDialogue/MessageDialogue';
import {
    FLOATING_MESSAGES_MOBILE_HEADER_SLOT_ID,
    FLOATING_PANELS_SHORT_VIEWPORT_QUERY,
    isCampaignContestDetailPath,
    SMALL_MOBILE_MEDIA_QUERY,
} from './floatingMessageUiConstants';

type FloatingMessagesThemeStyle = 'light' | 'dark';
const ACTIVE_ROW_BG = 'rgb(39, 39, 42)';

const FLOATING_SLIDE_WIDTH_PX = 340;
const FLOATING_SLIDE_HEIGHT_DEFAULT_PX = 550;

function getFloatingPanelSlideStyle(args: {
    isShortViewport: boolean;
    isExpanded: boolean;
    backgroundColor: string;
    border?: string;
}): React.CSSProperties {
    const bottom = args.isExpanded ? 0 : 10;
    const style: React.CSSProperties = {
        position: 'fixed',
        bottom,
        right: 15,
        zIndex: 1500,
        transformOrigin: 'bottom right',
        width: FLOATING_SLIDE_WIDTH_PX,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: args.backgroundColor,
    };
    if (args.border) {
        style.border = args.border;
    }
    if (args.isShortViewport) {
        style.height = 'min(480px, calc(100vh - 20px))';
        style.maxHeight = 'calc(100vh - 20px)';
    } else {
        style.height = FLOATING_SLIDE_HEIGHT_DEFAULT_PX;
    }
    return style;
}

function getFloatingMessagesPillSx(themeStyle: FloatingMessagesThemeStyle, isTablet: boolean): SxProps<Theme> {
    return {
        display: 'flex',
        alignItems: 'center',
        // Icon-only FAB: one flex child + space-between behaves like flex-start (icon drifts left).
        justifyContent: isTablet ? 'center' : 'space-between',
        gap: isTablet ? 0 : 2,
        px: isTablet ? 0 : 3.25,
        py: isTablet ? 0 : 1.35,
        borderRadius: isTablet ? '100%' : 999,
        backgroundColor: theme[themeStyle].deep_grey,
        color: theme[themeStyle].primary_white,
        boxShadow: '0px 6px 16px rgba(0,0,0,0.35)',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: theme[themeStyle].gray_text,
        },
        minWidth: isTablet ? 0 : 220,
        minHeight: isTablet ? 0 : 52,
        height: isTablet ? 72 : 'auto',
        width: isTablet ? 72 : 'auto',
        ...(isTablet && {
            flexShrink: 0,
            boxSizing: 'border-box',
        }),
    };
}

export interface IFloatingMessageButtonProps {
    clearAllOpenModals?: () => void;
    forceHeaderSlotOnTablet?: boolean;
}

export const FloatingMessageButton: React.FC<IFloatingMessageButtonProps> = ({
    clearAllOpenModals,
    forceHeaderSlotOnTablet = false,
}) => {
    const { themeStyle, device } = useThemeContext();
    const isSmallMobile = useMediaQuery(SMALL_MOBILE_MEDIA_QUERY);
    const isShortFloatingViewport = useMediaQuery(FLOATING_PANELS_SHORT_VIEWPORT_QUERY);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const floatingMessageOpen = useSelector(floatingMessageModalOpenSelector);
    const isExpanded = useSelector(isMessageModalExpandedSelector);
    const avatarMessageOpen = useSelector(avatarMessageOpenSelector);
    const { user_id } = getJWtDetails();
    const unreadMessages = useSelector(unreadMessageTotalCount);
    const currentRooms = useSelector(roomsSelector);
    const loading = useSelector(fetchingRoomsSelector);
    const [newMessageOpen, setNewMessageOpen] = useState(false);
    const wasNewMessageOpenRef = React.useRef(false);
    const [mobileHeaderSlotEl, setMobileHeaderSlotEl] = useState<HTMLElement | null>(null);

    const { activeRoomId, setActiveRoomId, limit, page } = useMessageContext();
    // Responsive breakpoints
    const isTablet = useMediaQuery('(max-width:767px) and (min-width:415px)');
    const isCampaignDetailPage = isCampaignContestDetailPath(location.pathname);
    const renderInHeaderSlot = isSmallMobile || (forceHeaderSlotOnTablet && isTablet) || isCampaignDetailPage;
    /** Header slot is icon-only (no pill label or avatar chips). */
    const headerIconOnly = renderInHeaderSlot;

    const handleViewAll = () => {
        // Save current page before navigating to /messages
        if (!sessionStorage.getItem('preMessagesRoute')) {
            sessionStorage.setItem('preMessagesRoute', location.pathname);
        }

        setActiveRoomId(null);

        batch(() => {
            dispatch(toggleFloatingMessageModal(false));
            dispatch(toggleIsMessageModalExpanded(true));
            dispatch(setMessagesExpandedReturnTarget('floating-list'));
            dispatch(toggleMessagenModal(false));
            dispatch(toggleAvatarMessageModal(false));
        });

        navigate(ROUTES.MESSAGES);
        handleClose();
    };

    const handleSetRoomId = (roomId: string) => {
        setActiveRoomId(roomId);
    };

    const handleLoadRooms = useCallback(() => {
        dispatch(getMyRooms({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleLoadRooms();
    }, [handleLoadRooms]);

    useEffect(() => {
        window.addEventListener('popstate', event => {
            if (event.state) {
                setActiveRoomId(null);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Safety reset: if expanded state gets stuck while leaving /messages,
    // the floating trigger would remain hidden. Always collapse it off-route.
    useEffect(() => {
        if (!location.pathname.startsWith(ROUTES.MESSAGES) && isExpanded) {
            dispatch(toggleIsMessageModalExpanded(false));
        }
    }, [location.pathname, isExpanded, dispatch]);

    useLayoutEffect(() => {
        if (!renderInHeaderSlot) {
            setMobileHeaderSlotEl(null);
            return;
        }
        setMobileHeaderSlotEl(document.getElementById(FLOATING_MESSAGES_MOBILE_HEADER_SLOT_ID));
    }, [renderInHeaderSlot]);

    const isCompactFab = isTablet || device.isMobile;

    const handleToggleMessages = () => {
        dispatch(
            retrieveUserRooms({
                data: {
                    query: {
                        user: user_id,
                        withUnreadOnly: unreadMessages > 0,
                    },
                },
            }),
        );
        dispatch(toggleFloatingMessageModal(!floatingMessageOpen));
        dispatch(toggleMessagenModal(false));

        clearAllOpenModals && clearAllOpenModals();
    };

    /** Matches `ChatBubbleIcon` on narrow phones: go straight to `/messages` (no list modal). */
    const handleFloatingEntryClick = () => {
        if (isSmallMobile) {
            handleViewAll();
            return;
        }
        handleToggleMessages();
    };

    const handleClose = () => {
        dispatch(toggleFloatingMessageModal(false));
        clearAllOpenModals && clearAllOpenModals();
    };

    const handleAvatarClick = (roomId: string) => {
        if (isSmallMobile) {
            handleRoomClick(roomId);
            return;
        }
        dispatch(toggleAvatarMessageModal(true));
        handleSetRoomId(roomId);
        dispatch(
            getMessagesForSelectedRoom({
                roomID: roomId,
                userID: user_id,
                pagination: {
                    limit,
                    page,
                },
            }),
        );
        handleClose();
    };

    const handleRoomClick = (roomId: string | undefined) => {
        navigate(`${ROUTES.MESSAGES}/${roomId}`);
        handleClose();
    };

    const handleNewMessage = () => {
        setNewMessageOpen(true);
    };

    // After composing/sending from the floating modal composer, refresh room list immediately.
    useEffect(() => {
        if (wasNewMessageOpenRef.current && !newMessageOpen) {
            dispatch(getMyRooms({}));
        }
        wasNewMessageOpenRef.current = newMessageOpen;
    }, [newMessageOpen, dispatch]);

    const openRoomFromPointer = (e: React.MouseEvent, roomId: string) => {
        e.preventDefault();
        e.stopPropagation();
        handleAvatarClick(roomId);
    };

    const swallowAvatarClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Defensive guard: never show rooms that do not include the logged-in user.
    const userScopedRooms = useMemo(() => {
        const getParticipantId = (participant: any) => {
            if (!participant) return '';
            if (typeof participant === 'string') return String(participant);
            if (typeof participant === 'object') {
                if (participant._id) return String(participant._id);
                if (participant.id) return String(participant.id);
            }
            return '';
        };
        const currentUserId = String(user_id ?? '');
        if (!currentUserId) return [];
        return (currentRooms ?? []).filter(room => {
            const senderId = getParticipantId(room?.sender);
            const receiverIds = Array.isArray(room?.receiver) ? room.receiver.map(getParticipantId) : [];
            return senderId === currentUserId || receiverIds.includes(currentUserId);
        });
    }, [currentRooms, user_id]);

    // Get only rooms visible to the current signed-in user.
    const unreadRooms = userScopedRooms;

    const floatingPanelBorder = '1px solid rgba(255,255,255,0.1)';
    const floatingHeaderSx = {
        p: 2,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: floatingPanelBorder,
    };
    const accentYellow = theme[themeStyle].true_highlight;
    const floatingIconButtonSx = {
        color: theme[themeStyle].primary_white,
        transition: 'color 0.15s ease',
        '&:hover': {
            color: accentYellow,
            backgroundColor: 'transparent',
        },
    };

    /** Same chrome as header `ChatBubbleIcon` (outline bubble, no FAB pill). */
    const mobileHeaderMessagesTriggerSx: SxProps<Theme> = {
        maxWidth: 'fit-content',
        minWidth: 'fit-content',
        width: 'auto',
        height: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderRadius: 0,
        px: 0,
        py: 0,
        cursor: 'pointer',
        position: 'relative',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    };

    const messagesTrigger = !isExpanded ? (
        <Box
            sx={
                renderInHeaderSlot
                    ? {
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'visible',
                      }
                    : {
                          position: 'fixed',
                          bottom: 24,
                          right: 24,
                          zIndex: 1300,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                      }
            }
            data-floating-messages-root>
            {unreadMessages > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        borderRadius: 50,
                        backgroundColor: theme[themeStyle].primary_red,
                        color: theme[themeStyle].true_white,
                        fontWeight: '600',
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        ...(headerIconOnly
                            ? {
                                  top: 0,
                                  right: 0,
                                  transform: 'translate(45%, -45%)',
                                  minWidth: 25,
                                  height: 25,
                                  fontSize: 12,
                                  padding: '0 6px',
                              }
                            : {
                                  top: renderInHeaderSlot ? -3 : -5,
                                  right: renderInHeaderSlot ? -3 : -8,
                                  minWidth: 28,
                                  height: 28,
                                  fontSize: 13,
                                  padding: '0 6px',
                              }),
                    }}>
                    {cutoffAndAddPlus(unreadMessages, 99)}
                </Box>
            )}
            <Box
                sx={
                    renderInHeaderSlot
                        ? mobileHeaderMessagesTriggerSx
                        : getFloatingMessagesPillSx(themeStyle as FloatingMessagesThemeStyle, isCompactFab)
                }
                onClick={handleFloatingEntryClick}
                aria-label="Messages">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: headerIconOnly || isCompactFab ? 0 : 1.25,
                        cursor: 'pointer',
                        lineHeight: 0,
                        ...(isCompactFab &&
                            !isSmallMobile &&
                            !headerIconOnly && {
                                width: '100%',
                                height: '100%',
                                '& svg': { display: 'block' },
                            }),
                    }}>
                    {headerIconOnly ? (
                        <QuickMessageIcon stroke={theme[themeStyle].form_label} height={34} width={34} />
                    ) : (
                        <QuickMessageIcon width={isCompactFab ? 30 : 28} height={isCompactFab ? 30 : 28} />
                    )}
                    {!isCompactFab && !headerIconOnly && (
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: 17,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].primary_white,
                                textTransform: 'none',
                                letterSpacing: '0.01em',
                            }}>
                            Messages
                        </Typography>
                    )}
                </Box>

                {!isCompactFab && !headerIconOnly && (
                    <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 2000, cursor: 'pointer' }}>
                        {userScopedRooms?.slice(0, 2).map((room, index) => {
                            const allUsers = [...room.receiver, room.sender].filter(u => u._id !== user_id);
                            const displayName = `${allUsers[0]?.firstName ?? ''} ${allUsers[0]?.lastName ?? ''}`.trim();
                            return (
                                <Box
                                    key={room._id}
                                    component="button"
                                    type="button"
                                    title={displayName}
                                    aria-label={displayName || 'Open conversation'}
                                    onMouseDown={e => openRoomFromPointer(e, room._id)}
                                    onClick={swallowAvatarClick}
                                    sx={{
                                        border: 'none',
                                        background: 'none',
                                        padding: 0,
                                        margin: 0,
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        lineHeight: 0,
                                        borderRadius: '50%',
                                        display: 'inline-flex',
                                        transition: 'transform 0.2s ease',
                                        '&:hover': { transform: 'scale(1.1)' },
                                        '&:focus-visible': {
                                            outline: `2px solid ${theme[themeStyle].true_highlight}`,
                                            outlineOffset: 2,
                                        },
                                    }}>
                                    <Box sx={{ pointerEvents: 'none', display: 'inline-flex' }}>
                                        <ThspianAvatar
                                            user={allUsers[0]}
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                border: `2px solid ${theme[themeStyle].deep_grey}`,
                                                ml: index > 0 ? -1.25 : 0,
                                            }}
                                            withMonogram
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    ) : null;

    return (
        <Box sx={{ position: 'relative' }}>
            <Box>
                {renderInHeaderSlot && mobileHeaderSlotEl && messagesTrigger
                    ? createPortal(messagesTrigger, mobileHeaderSlotEl)
                    : !renderInHeaderSlot
                    ? messagesTrigger
                    : null}
            </Box>
            {!isSmallMobile && (
                <Slide
                    direction="up"
                    in={avatarMessageOpen}
                    mountOnEnter
                    unmountOnExit
                    style={{
                        ...getFloatingPanelSlideStyle({
                            isShortViewport: isShortFloatingViewport,
                            isExpanded,
                            backgroundColor: ACTIVE_ROW_BG,
                            border: floatingPanelBorder,
                        }),
                        borderRadius: 16,
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                            {loading ? (
                                <List sx={{ p: 0 }}>
                                    {Array(3)
                                        .fill(0)
                                        .map((_, index) => (
                                            <RoomItemSkeleton
                                                key={`avatar-skeleton-${index}`}
                                                showDivider={index !== 2}
                                            />
                                        ))}
                                </List>
                            ) : unreadRooms && unreadRooms.length === 0 ? (
                                <EmptySearchBlock
                                    title="No unread messages"
                                    subTitle="You're all caught up!"
                                    mainHeaderStyle={{
                                        fontSize: 14,
                                    }}
                                    actionButton={
                                        <Button
                                            fullWidth
                                            sx={{
                                                mt: 2,
                                                mb: 2,
                                                color: theme[themeStyle].button_gradient.end,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }}>
                                            View all messages
                                        </Button>
                                    }
                                />
                            ) : avatarMessageOpen ? (
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        width: '100%',
                                        overflow: 'auto',
                                        backgroundColor: 'transparent',
                                    }}>
                                    <FloatingConversationView
                                        roomId={activeRoomId}
                                        onRoomClick={handleRoomClick}
                                        onClickMessageButton={handleToggleMessages}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </Slide>
            )}

            {!isSmallMobile && (
                <Slide
                    direction="up"
                    in={floatingMessageOpen}
                    mountOnEnter
                    unmountOnExit
                    style={{
                        ...getFloatingPanelSlideStyle({
                            isShortViewport: isShortFloatingViewport,
                            isExpanded,
                            backgroundColor: theme[themeStyle].deep_grey,
                            border: floatingPanelBorder,
                        }),
                        borderRadius: 16,
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            height: '100%',
                            position: 'relative',
                        }}>
                        <Box sx={floatingHeaderSx}>
                            <Typography
                                sx={{
                                    fontSize: '1.25rem',
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].primary_white,
                                }}>
                                Messages
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={handleViewAll} sx={floatingIconButtonSx} size="medium">
                                    <ExpandIcon height={24} width={24} stroke="currentColor" />
                                </IconButton>
                                <IconButton onClick={handleToggleMessages} sx={floatingIconButtonSx} size="medium">
                                    <CloseIcon sx={{ color: 'inherit' }} />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                            }}>
                            {loading ? (
                                <List sx={{ p: 0 }}>
                                    {Array(3)
                                        .fill(0)
                                        .map((_, index) => (
                                            <RoomItemSkeleton
                                                key={`floating-skeleton-${index}`}
                                                showDivider={index !== 2}
                                            />
                                        ))}
                                </List>
                            ) : unreadRooms && unreadRooms.length === 0 ? (
                                <EmptySearchBlock
                                    title="No unread messages"
                                    subTitle="You're all caught up!"
                                    mainHeaderStyle={{
                                        fontSize: 14,
                                    }}
                                    actionButton={
                                        <Button
                                            fullWidth
                                            onClick={handleViewAll}
                                            sx={{
                                                mt: 2,
                                                mb: 2,
                                                color: theme[themeStyle].button_gradient.end,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }}>
                                            View all messages
                                        </Button>
                                    }
                                />
                            ) : (
                                <>
                                    <List sx={{ p: 0 }}>
                                        {unreadRooms &&
                                            unreadRooms.map((room, index) => (
                                                <RoomItem
                                                    key={room._id}
                                                    room={room}
                                                    onClick={handleAvatarClick}
                                                    isActive={activeRoomId === room._id}
                                                    showDivider={index !== unreadRooms.length - 1}
                                                    visualVariant="floatingPanel"
                                                />
                                            ))}
                                    </List>

                                    {newMessageOpen && (
                                        <MessageDialogue
                                            open={newMessageOpen}
                                            fromModal
                                            onClose={() => setNewMessageOpen(false)}
                                        />
                                    )}

                                    <Box
                                        sx={{
                                            position: 'fixed',
                                            bottom: 20,
                                            right: 30,
                                            zIndex: 2000,
                                        }}>
                                        <IconButton
                                            onClick={handleNewMessage}
                                            aria-label="New message"
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '12px',
                                                color: theme[themeStyle].primary_white,
                                                backgroundColor: theme[themeStyle].secondaryBg,
                                                border: floatingPanelBorder,
                                                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                                },
                                            }}>
                                            <ComposeMessageIcon width={22} height={22} stroke={accentYellow} />
                                        </IconButton>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Slide>
            )}
        </Box>
    );
};
